import { useEffect, useRef } from "react";
import { Alert, AlertTitle, Box, Typography } from "@mui/material";
import { useToastAlertStore } from "../../state/ToastAlertStore";
import { AlertColor } from "../../customEnums/AlertColor";
import { getAlertStyle, getAlertTextColor } from "./AlertStyles";

const getAriaLive = (severity: AlertColor) =>
    severity === AlertColor.ERROR || severity === AlertColor.WARNING ? "assertive" : "polite";

export const ToastAlert = () => {
    const alerts = useToastAlertStore((state) => state.alerts);
    const dismissAlert = useToastAlertStore((state) => state.dismissAlert);
    const timersRef = useRef<Map<string, number>>(new Map());

    useEffect(() => {
        const activeIds = new Set(alerts.map((alert) => alert.id));

        alerts.forEach((alert) => {
            if (alert.autoCloseTime && !timersRef.current.has(alert.id)) {
                const timerId = window.setTimeout(() => {
                    dismissAlert(alert.id);
                }, alert.autoCloseTime);
                timersRef.current.set(alert.id, timerId);
            }
        });

        timersRef.current.forEach((timerId, id) => {
            if (!activeIds.has(id)) {
                clearTimeout(timerId);
                timersRef.current.delete(id);
            }
        });

        return () => {
            timersRef.current.forEach((timerId) => clearTimeout(timerId));
            timersRef.current.clear();
        };
    }, [alerts, dismissAlert]);

    if (alerts.length === 0) return null;

    return (
        <Box
            sx={{
                position: "fixed",
                top: 70,
                right: 20,
                zIndex: 300000,
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
            }}
        >
            {alerts.map((alert) => {
                const textColor = getAlertTextColor(alert.severity);
                const alertStyle = {
                    ...getAlertStyle(alert.severity),
                    width: "384px",
                    ...(alert.severity === AlertColor.WARNING
                        ? { "& .MuiAlert-action": { color: textColor } }
                        : {}),
                };

                return (
                    <Alert
                        key={alert.id}
                        severity={alert.severity}
                        sx={alertStyle}
                        onClose={alert.dismissible ? () => dismissAlert(alert.id) : undefined}
                        role="alert"
                        aria-live={getAriaLive(alert.severity)}
                        aria-atomic="true"
                    >
                        <AlertTitle>
                            <Typography color={textColor} variant="body1">
                                {alert.title}
                            </Typography>
                        </AlertTitle>
                        {alert.message && (
                            <Typography color={textColor} variant="body2">
                                {alert.message}
                            </Typography>
                        )}
                    </Alert>
                );
            })}
        </Box>
    );
};